import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { showToast } from "../components/toast";
import { ApiError } from "./api-request";
import { clearStoredSession } from "./clear-session";

// Global error handling: one place to toast unexpected failures. A query or
// mutation can opt out by passing `meta: { silent: true }` and handling the
// error itself (inline UI or a custom `onError`).
function toastError(error: unknown, silent?: boolean) {
  if (silent) return;
  const message =
    error instanceof ApiError
      ? error.message
      : (error as Error)?.message === "Failed to fetch"
        ? "Network error, please check your connection."
        : "An error occurred, please try again";
  showToast(message, false, { position: "top-right" });
}

// Once we start redirecting for an expired session, ignore further 401s so we
// don't stack toasts/navigations. A full-page redirect reloads this module and
// resets the flag.
let handlingExpiredSession = false;

// A 401 while a token is present means the session expired or was revoked
// server-side (the client can't validate token expiry itself). Clear the stale
// session so nothing keeps refetching with a dead token, then send the user to
// sign in — unless they are already on a public/auth page, where we clear
// silently and let them keep browsing logged-out.
// Returns true if the 401 was handled here (so callers skip the generic toast).
function handleUnauthorized(): boolean {
  if (typeof window === "undefined") return false;
  // No token => this isn't an expired session (e.g. a failed login attempt).
  if (!localStorage.getItem("token")) return false;

  clearStoredSession();

  const path = window.location.pathname;
  const onPublicPage =
    path === "/" ||
    path.startsWith("/signin") ||
    path.startsWith("/signup");

  if (!onPublicPage && !handlingExpiredSession) {
    handlingExpiredSession = true;
    showToast("Your session has expired. Please sign in again.", false, {
      position: "top-right",
    });
    window.location.href = "/signin";
  }
  return true;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 min — navigating back doesn't refetch instantly
      gcTime: 5 * 60_000,
      // Don't retry an expired/revoked session — retrying just repeats the 401.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.status === 401
          ? false
          : failureCount < 1,
      refetchOnWindowFocus: false, // app has its own 30-min auto-reload
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (error instanceof ApiError && error.status === 401) {
        if (handleUnauthorized()) return;
      }
      toastError(error, query.meta?.silent as boolean | undefined);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (error instanceof ApiError && error.status === 401) {
        if (handleUnauthorized()) return;
      }
      toastError(error, mutation.meta?.silent as boolean | undefined);
    },
  }),
});
